import { RefObject, useCallback, useRef, useState } from 'react';

const MIN_SCALE = 1;
const MAX_SCALE = 4;
const TAP_SLOP_PX = 8;
const TAP_MAX_MS = 300;
const DOUBLE_TAP_MS = 350;
const DOUBLE_TAP_SLOP_PX = 24;

/**
 * Pinch, drag and double-tap zoom for the enlarged image viewer. Gesture bookkeeping lives in
 * refs because pointer handlers must read the latest values synchronously between renders.
 */
export function usePinchZoom(
  surfaceRef: RefObject<HTMLDivElement | null>,
  enlargedRef: RefObject<HTMLImageElement | null>,
  disabled: boolean,
) {
  const [zoom, setZoom] = useState({ scale: 1, x: 0, y: 0 });
  const zoomRef = useRef(zoom);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const gesture = useRef<{
    scale: number;
    x: number;
    y: number;
    cx: number;
    cy: number;
    distance: number;
  } | null>(null);
  const tap = useRef<{ x: number; y: number; time: number } | null>(null);
  const lastTap = useRef<{ x: number; y: number; time: number } | null>(null);
  const lastPointerType = useRef('mouse');
  const resetZoom = useCallback(() => {
    const next = { scale: 1, x: 0, y: 0 };
    zoomRef.current = next;
    setZoom(next);
    pointers.current.clear();
    gesture.current = null;
    tap.current = null;
    lastTap.current = null;
  }, []);
  const applyZoom = (scale: number, x: number, y: number) => {
    scale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, scale));
    const img = enlargedRef.current;
    const viewport = surfaceRef.current;
    if (!img || !viewport) return;
    const fit =
      img.naturalWidth && img.naturalHeight
        ? Math.min(img.clientWidth / img.naturalWidth, img.clientHeight / img.naturalHeight)
        : 1;
    const width = img.naturalWidth ? img.naturalWidth * fit : img.clientWidth;
    const height = img.naturalHeight ? img.naturalHeight * fit : img.clientHeight;
    const maxX = Math.max(0, (width * scale - viewport.clientWidth) / 2);
    const maxY = Math.max(0, (height * scale - viewport.clientHeight) / 2);
    const next = {
      scale,
      x: Math.max(-maxX, Math.min(maxX, x)),
      y: Math.max(-maxY, Math.min(maxY, y)),
    };
    zoomRef.current = next;
    setZoom(next);
  };
  const toggleZoom = () => applyZoom(zoomRef.current.scale > 1 ? 1 : 2, 0, 0);
  const startGesture = () => {
    const points = Array.from(pointers.current.values());
    if (!points.length) {
      gesture.current = null;
      return;
    }
    const a = points[0];
    const b = points[1] ?? a;
    const rect = surfaceRef.current!.getBoundingClientRect();
    gesture.current = {
      ...zoomRef.current,
      cx: (a.x + b.x) / 2 - rect.left - rect.width / 2,
      cy: (a.y + b.y) / 2 - rect.top - rect.height / 2,
      distance: Math.hypot(a.x - b.x, a.y - b.y),
    };
  };
  const pointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    lastPointerType.current = event.pointerType;
    if (disabled || (event.pointerType === 'mouse' && event.button !== 0)) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    pointers.current.set(event.pointerId, {
      x: event.clientX,
      y: event.clientY,
    });
    tap.current =
      pointers.current.size === 1 ? { x: event.clientX, y: event.clientY, time: Date.now() } : null;
    startGesture();
  };
  const pointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!pointers.current.has(event.pointerId) || !gesture.current) return;
    pointers.current.set(event.pointerId, {
      x: event.clientX,
      y: event.clientY,
    });
    if (tap.current && Math.hypot(event.clientX - tap.current.x, event.clientY - tap.current.y) > TAP_SLOP_PX)
      tap.current = null;
    const points = Array.from(pointers.current.values());
    const a = points[0];
    const b = points[1] ?? a;
    const rect = event.currentTarget.getBoundingClientRect();
    const cx = (a.x + b.x) / 2 - rect.left - rect.width / 2;
    const cy = (a.y + b.y) / 2 - rect.top - rect.height / 2;
    const start = gesture.current;
    const scale = Math.max(
      MIN_SCALE,
      Math.min(
        MAX_SCALE,
        points.length > 1 && start.distance > 0
          ? (start.scale * Math.hypot(a.x - b.x, a.y - b.y)) / start.distance
          : start.scale,
      ),
    );
    applyZoom(
      scale,
      cx - ((start.cx - start.x) * scale) / start.scale,
      cy - ((start.cy - start.y) * scale) / start.scale,
    );
  };
  const pointerEnd = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!pointers.current.has(event.pointerId)) return;
    if (
      event.type === 'pointerup' &&
      event.pointerType !== 'mouse' &&
      tap.current &&
      Date.now() - tap.current.time < TAP_MAX_MS
    ) {
      const previous = lastTap.current;
      if (
        previous &&
        Date.now() - previous.time < DOUBLE_TAP_MS &&
        Math.hypot(previous.x - event.clientX, previous.y - event.clientY) < DOUBLE_TAP_SLOP_PX
      ) {
        toggleZoom();
        lastTap.current = null;
      } else
        lastTap.current = {
          x: event.clientX,
          y: event.clientY,
          time: Date.now(),
        };
    }
    tap.current = null;
    pointers.current.delete(event.pointerId);
    startGesture();
  };

  return {
    zoom,
    resetZoom,
    toggleZoom,
    lastPointerType,
    handlers: {
      onPointerDown: pointerDown,
      onPointerMove: pointerMove,
      onPointerUp: pointerEnd,
      onPointerCancel: pointerEnd,
      onLostPointerCapture: pointerEnd,
    },
  };
}
